import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, BanknoteIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, PencilIcon, Trash2Icon, SaveIcon, XIcon, PlusIcon, ListIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, PackageIcon, UploadIcon, PaperclipIcon, EyeIcon, EyeOffIcon, CheckIcon, EditIcon, FileIcon, DownloadIcon, XCircle } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
interface TaskDetailProps {
  task: Task;
  customers: Customer[];
  onBack: () => void;
  onSave: (updatedTask: Task) => void;
  isReadOnly?: boolean;
}
export function TaskDetail({
  task,
  customers,
  onBack,
  onSave,
  isReadOnly = false
}: TaskDetailProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [editingMaterialValue, setEditingMaterialValue] = useState('');
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLogHours, setWorkLogHours] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
    hours?: number;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  const [editingWorkLogIndex, setEditingWorkLogIndex] = useState<number | null>(null);
  const [editingWorkLogDate, setEditingWorkLogDate] = useState('');
  const [editingWorkLogDescription, setEditingWorkLogDescription] = useState('');
  const [editingWorkLogHours, setEditingWorkLogHours] = useState('');
  // Attachments state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    pricing: true,
    materials: true,
    workLog: true,
    attachments: true
  });
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  // Load standard materials
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setChargerPrices(docSnap.data() as any);
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (task?.category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, task?.category, chargerPrices]);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setCustomerId(task.customerId);
      setDescription(task.description || '');
      setStatus(task.status);
      setChargerType(task.chargerType || 'Zaptec 1');
      setTaskType(task.taskType || 'Tilbudsopgave');
      if (task.agreedPlanning) {
        const [date, time] = task.agreedPlanning.split('T');
        setAgreedPlanning(date);
        setAgreedPlanningTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedPlanning('');
        setAgreedPlanningTime('09:00');
      }
      if (task.agreedInstallation) {
        const [date, time] = task.agreedInstallation.split('T');
        setAgreedInstallation(date);
        setAgreedInstallationTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedInstallation('');
        setAgreedInstallationTime('09:00');
      }
      setCarDeliveryDate(task.carDeliveryDate || '');
      setHours(task.hours?.toString() || '');
      setMaterialPrice(task.materialPrice?.toString() || '');
      setHourlyRate(task.hourlyRate?.toString() || '');
      setQuotePrice(task.quotePrice?.toString() || '');
      setInvoiced(task.invoiced ? 'yes' : 'no');
      setSubTasks(task.subTasks || '');
      setMaterials(task.materials || []);
      setMaterialInput('');
      setMaterialPriceInput('');
      setBulkInput('');
      setBulkPrice('');
      setShowMaterials(false);
      setBulkMode(false);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
      setWorkLog(task.workLog || []);
      setWorkLogDate(new Date().toISOString().split('T')[0]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setShowWorkLog(false);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
      setAttachments(task.attachments || []);
    }
  }, [task]);
  const handleAddPresetMaterial = (preset: StandardMaterial) => {
    if (isReadOnly) return;
    setMaterials([...materials, preset.name]);
    if (preset.price !== undefined) {
      const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
      const newTotalPrice = currentPrice + preset.price;
      setMaterialPrice(newTotalPrice.toString());
    }
  };
  const totalHoursWithWorkLog = useMemo(() => {
    const baseHours = hours ? parseFloat(hours) : 0;
    const workLogHoursTotal = workLog.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    return baseHours + workLogHoursTotal;
  }, [hours, workLog]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = totalHoursWithWorkLog;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const totalLaborCost = rate * time;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? totalLaborCost : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      return {
        totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, totalHoursWithWorkLog]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        setMaterialPrice((currentPrice + additionalPrice).toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          setMaterialPrice((currentPrice + parseFloat(bulkPrice)).toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => setMaterials(materials.filter((_, i) => i !== index));
  const handleStartEditMaterial = (index: number) => {
    setEditingMaterialIndex(index);
    setEditingMaterialValue(materials[index]);
  };
  const handleSaveEditMaterial = () => {
    if (editingMaterialIndex !== null && editingMaterialValue.trim()) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingMaterialIndex] = editingMaterialValue.trim();
      setMaterials(updatedMaterials);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
    }
  };
  const handleCancelEditMaterial = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialValue('');
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      const entry: any = {
        date: workLogDate,
        description: workLogDescription.trim()
      };
      if (workLogHours.trim()) entry.hours = parseFloat(workLogHours);
      setWorkLog([...workLog, entry]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => setWorkLog(workLog.filter((_, i) => i !== index));
  const handleStartEditWorkLog = (index: number) => {
    const entry = workLog[index];
    setEditingWorkLogIndex(index);
    setEditingWorkLogDate(entry.date);
    setEditingWorkLogDescription(entry.description);
    setEditingWorkLogHours(entry.hours?.toString() || '');
  };
  const handleSaveEditWorkLog = () => {
    if (editingWorkLogIndex !== null && editingWorkLogDescription.trim() && editingWorkLogDate) {
      const updatedWorkLog = [...workLog];
      const entry: any = {
        date: editingWorkLogDate,
        description: editingWorkLogDescription.trim()
      };
      if (editingWorkLogHours.trim()) entry.hours = parseFloat(editingWorkLogHours);
      updatedWorkLog[editingWorkLogIndex] = entry;
      setWorkLog(updatedWorkLog);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
    }
  };
  const handleCancelEditWorkLog = () => {
    setEditingWorkLogIndex(null);
    setEditingWorkLogDate('');
    setEditingWorkLogDescription('');
    setEditingWorkLogHours('');
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      setIsUploading(true);
      const storagePath = `tasks/${task.id}/bilag/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newAttachment: TaskAttachment = {
        name: file.name,
        url,
        storagePath,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Der opstod en fejl under upload af filen.');
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachment: TaskAttachment, index: number) => {
    if (!confirm('Er du sikker på, at du vil slette dette bilag?')) return;
    try {
      const storageRef = ref(storage, attachment.storagePath);
      await deleteObject(storageRef);
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Der opstod en fejl under sletning af filen.');
    }
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const toggleAllSections = () => {
    const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
    setExpandedSections({
      pricing: !allExpanded,
      materials: !allExpanded,
      workLog: !allExpanded,
      attachments: !allExpanded
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !title.trim() || !customerId) return;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onSave({
      ...task,
      title,
      customerId,
      customerName: customer.name,
      description,
      status,
      chargerType: task.category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    });
    onBack();
  };
  if (!task) return null;
  const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-2022" data-mp="atnd5f3sbfpe">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2023" data-mp="asuaap7y5acj">
        <button onClick={onBack} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors mb-6" data-id="element-2024" data-mp="2zlzklv8zgqgc">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2025" data-mp="2x0iettahi2bb" />
          <span className="font-medium" data-id="element-2026" data-mp="2x1ti7s00p81w">Tilbage</span>
        </button>

        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap" data-id="element-2027" data-mp="3204su4y4zml6">
          <div data-id="element-2028" data-mp="2n2cdbcslay6z">
            <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-2029" data-mp="2n2u6u1nvayb7">
              Rediger opgave
            </h1>
            {task?.taskId && <p className="text-stone-500 mt-2" data-id="element-2030" data-mp="2s3i8dh9q2xjo">
                Opgave-ID:{' '}
                <span className="font-medium text-teal-600" data-id="element-2031" data-mp="2e8fw8cea8byf">{task.taskId}</span>
              </p>}
          </div>
          {!isReadOnly && <Button type="button" variant="primary" onClick={handleSubmit as any} className="hidden sm:flex" data-id="element-2032" data-mp="24ccu36jfkdbh">
              Gem ændringer
            </Button>}
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 sm:p-8" data-id="element-2033" data-mp="1vby4huzfoc26">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2034" data-mp="1zc87qzvucodc">
            {/* Basic Information */}
            <div data-id="element-2035" data-mp="1gopqkmeqpsmi">
              <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-2036" data-mp="1gqajy8pbj5is">
                Grundlæggende information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2037" data-mp="1ppnlmf77apsy">
                <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required disabled={isReadOnly} data-id="element-2038" data-mp="1pmrouu73a763" />

                <Select label="Kunde" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                value: '',
                label: 'Vælg en kunde'
              }, ...customers.map(c => ({
                value: c.id,
                label: `${c.name} (${c.customerId})`
              }))]} required disabled={isReadOnly} data-id="element-2039" data-mp="1umea9qx2mtpz" />
              </div>

              <div className="mt-4" data-id="element-2040" data-mp="3oulqb2rlp8qe">
                <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} disabled={isReadOnly} data-id="element-2041" data-mp="3trkmdqutrpr8" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-2042" data-mp="363b5n3cgxs6y">
                <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                value: 'planned',
                label: 'Planlagt'
              }, {
                value: 'pending',
                label: 'Afventende'
              }, {
                value: 'in-progress',
                label: 'I gang'
              }, {
                value: 'done',
                label: 'Afsluttet'
              }]} disabled={isReadOnly} data-id="element-2043" data-mp="3b0uibytipwws" />

                <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                value: 'no',
                label: 'Nej'
              }, {
                value: 'yes',
                label: 'Ja'
              }]} disabled={isReadOnly} data-id="element-2044" data-mp="1n4d7qeb28mez" />
              </div>
            </div>

            {/* Dates */}
            <div data-id="element-2045" data-mp="3l4tpyu2xlglr">
              <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-2046" data-mp="3l4ahs53326kx">
                Datoer
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2047" data-mp="3g57aedzmro9s">
                <div data-id="element-2048" data-mp="3b7flzty72kac">
                  <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} disabled={isReadOnly} data-id="element-2049" data-mp="3b7xmj2rw3ko2" />
                  {agreedPlanning && <div className="mt-2" data-id="element-2050" data-mp="2xhc57ii33isa">
                      <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} disabled={isReadOnly} data-id="element-2051" data-mp="2sg4tj8ctlzgc" />
                    </div>}
                </div>

                <div data-id="element-2052" data-mp="1806t3v4wqnsr">
                  <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} disabled={isReadOnly} data-id="element-2053" data-mp="1cxq8iydcd6ma" />
                  {agreedInstallation && <div className="mt-2" data-id="element-2054" data-mp="p8nr9rmmdfsb">
                      <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} disabled={isReadOnly} data-id="element-2055" data-mp="p5i4wk5dks0a" />
                    </div>}
                </div>
              </div>

              {task.category === 'Ladeboks' && <div className="mt-4" data-id="element-2056" data-mp="13vpo6ht32l3m">
                  <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} disabled={isReadOnly} data-id="element-2057" data-mp="13uocqc85nwld" />
                  <p className="text-xs text-stone-500 mt-1" data-id="element-2058" data-mp="pwzqazyfwhe7">
                    Angiv hvornår bilen leveres til kunden
                  </p>
                </div>}
            </div>

            {/* Expand/Collapse All Button */}
            {!isReadOnly && <div className="flex justify-end" data-id="element-2059" data-mp="uv9l39g95fza">
                <button type="button" onClick={toggleAllSections} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-2060" data-mp="utggs0t437ug">
                  {allExpanded ? <>
                      <ChevronUpIcon className="w-4 h-4" data-id="element-2061" data-mp="2isiu0cjoz2b2" />
                      Skjul alle sektioner
                    </> : <>
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2062" data-mp="23xmfxip7bkq2" />
                      Udvid alle sektioner
                    </>}
                </button>
              </div>}

            {/* PRICING SECTION */}
            <AnimatePresence data-id="element-2063" data-mp="31jtfge6nemvk">
              {expandedSections.pricing ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2064" data-mp="2mudu5dpvz9n9">
                  <div data-id="element-2065" data-mp="1wjdddi20jg2x">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  pricing: false
                })} className="flex items-center justify-between w-full mb-6 group" data-id="element-2066" data-mp="1rmd7p892sfcc">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2067" data-mp="2acuwj1qseqsf">
                        Priser og timer
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2068" data-mp="25l35yr7msl2a">
                        {totalHoursWithWorkLog > 0 && <span className="text-sm text-stone-500" data-id="element-2069" data-mp="20mrts9tv5ygw">
                            Total: {totalHoursWithWorkLog.toFixed(1)}t
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2070" data-mp="2ta5sodh4h43n" />
                      </div>
                    </button>

                    <div className="space-y-6" data-id="element-2071" data-mp="35mzt1est0wie">
                      {task.category === 'Ladeboks' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-2" data-id="element-2072" data-mp="3ak9ujstgwyml">
                          <label className="block text-sm font-medium text-stone-700" data-id="element-2073" data-mp="3tc2mrl0pqft9">
                            Ladeboks type
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2074" data-mp="74ujco9csizv">
                            {(['Zaptec 1', 'Zaptec 2', 'Andet'] as const).map(type => <button key={type} type="button" onClick={() => setChargerType(type)} disabled={isReadOnly} className={`p-4 rounded-xl border text-left transition-all ${chargerType === type ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500' : 'bg-white border-stone-200 hover:border-stone-300'} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2075" data-mp="3ijuzvvtahlmy">
                                  <div className="font-medium text-stone-900" data-id="element-2076" data-mp="f3xo5ifhc5mi">
                                    {type}
                                  </div>
                                  {type !== 'Andet' && <div className="text-sm text-stone-500 mt-1" data-id="element-2077" data-mp="2guisei8ztplf">
                                      {chargerPrices[type].toLocaleString('da-DK')}{' '}
                                      kr
                                    </div>}
                                </button>)}
                          </div>
                        </motion.div>}

                      <div className="pt-2" data-id="element-2078" data-mp="ml3unr1qigpf">
                        <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-2079" data-mp="8mmbm6mrxsdu">
                          Opgavetype
                        </label>
                        <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-2080" data-mp="3uuvnr17ofzls">
                          {(['Tilbudsopgave', 'Regningsopgave'] as const).map(type => <button key={type} type="button" onClick={() => setTaskType(type)} disabled={isReadOnly} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${taskType === type ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'} ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2081" data-mp="1t30oxm6irpkz">
                                {type}
                              </button>)}
                        </div>
                      </div>

                      <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200" data-id="element-2082" data-mp="3p9z5mgt91f8t">
                        <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide mb-6" data-id="element-2083" data-mp="3kavwola5eovk">
                          Timer & Pris
                        </h3>

                        {taskType === 'Tilbudsopgave' ? <div className="space-y-6" data-id="element-2084" data-mp="1ij9en988i4b8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2085" data-mp="1ihojpii0cjcu">
                              <div data-id="element-2086" data-mp="1q2s5u2rmt3uw">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2087" data-mp="1v1vavn7m3a2v">
                                  Tilbudspris (kr){' '}
                                  <span className="text-red-500" data-id="element-2088" data-mp="1ztn0nviy1bry">*</span>
                                </label>
                                <input type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2089" data-mp="24u138y1rztil" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2090" data-mp="3dv3z2rvbxqdx">
                                  Den samlede pris kunden skal betale
                                </p>
                              </div>

                              <div data-id="element-2091" data-mp="34twax2mj25xk">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2092" data-mp="34suoy33z3z12">
                                  Materialer pris (kr)
                                </label>
                                <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2093" data-mp="2uxjh84qzfs2t" />
                                {task.category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-2094" data-mp="36ce78e8kae2d">
                                    Udfyldes automatisk baseret på ladeboks type
                                  </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-2095" data-mp="3bztcsjcq5a4o">
                                    Dine udgifter til materialer
                                  </p>}
                              </div>
                            </div>

                            <div className="pt-4 border-t border-stone-200 space-y-6" data-id="element-2096" data-mp="3l2uem7uk4f2j">
                              <div data-id="element-2097" data-mp="3pxhtrg3o7x4g">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2098" data-mp="3uz78p6u0kuxt">
                                  Timepris (kr)
                                </label>
                                <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2099" data-mp="8jcqxbfiaa0r" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2100" data-mp="ip49e1ap2su3">
                                  Din pris per time (bruges til udregning)
                                </p>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2101" data-mp="15nh7w2q8tybi">
                                <div data-id="element-2102" data-mp="15l4jb6pzcpph">
                                  <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2103" data-mp="11mox351q3ld4">
                                    Timer brugt
                                  </label>
                                  <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2104" data-mp="1tcjr3f0izz84" />
                                  <p className="text-xs text-stone-500 mt-1.5" data-id="element-2105" data-mp="1nu16oj4vmlcq">
                                    Bruges til at udregne din reelle timepris
                                  </p>
                                </div>

                                <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-2106" data-mp="2fiug9y0rx0dc">
                                  <div data-id="element-2107" data-mp="8w5b4jsom7qy">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2108" data-mp="8v3nlfuhbssc">
                                      Samlet pris
                                    </p>
                                    <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-2109" data-mp="ikwy0divy1v0">
                                      {calculations.totalPrice.toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                  </div>
                                  <div className="pt-3 border-t border-stone-100" data-id="element-2110" data-mp="1b9avms62aw61">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2111" data-mp="16ahhqymv535c">
                                      Pris kun for timer
                                    </p>
                                    <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2112" data-mp="1icshj1b3onf4">
                                      {(calculations.totalPrice - (materialPrice ? parseFloat(materialPrice) : 0)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                    <p className="text-xs text-stone-400 mt-1" data-id="element-2113" data-mp="bwozd0zp6qyy">
                                      Tilbudspris − Materialer
                                    </p>
                                  </div>
                                  {totalHoursWithWorkLog > 0 && <div className="pt-3 border-t border-stone-100" data-id="element-2114" data-mp="2q59ciuymb18k">
                                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2115" data-mp="2l5wow7zx9q4x">
                                        Timer brugt i alt
                                      </p>
                                      <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2116" data-mp="2b8id4loy1piy">
                                        {totalHoursWithWorkLog.toFixed(1)} timer
                                      </p>
                                    </div>}
                                </div>
                              </div>
                            </div>
                          </div> : <div className="space-y-6" data-id="element-2117" data-mp="1sjlek5mikaux">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2118" data-mp="1njz23yeieyn6">
                              <div data-id="element-2119" data-mp="3cclfvrb6h4zq">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2120" data-mp="3cdmsi07udzjk">
                                  Timepris (kr){' '}
                                  <span className="text-red-500" data-id="element-2121" data-mp="32fgv9vwzdhl1">*</span>
                                </label>
                                <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2122" data-mp="37cpfs35lpxpg" />
                                <p className="text-xs text-stone-500 mt-1.5" data-id="element-2123" data-mp="1r30zpqesh96q">
                                  Din pris per time
                                </p>
                              </div>

                              <div data-id="element-2124" data-mp="200t80am3fgkm">
                                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2125" data-mp="2e25nkkxcdg5z">
                                  Materialer pris (kr)
                                </label>
                                <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2126" data-mp="29yr4fle3wjob" />
                                {task.category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-2127" data-mp="3o66p9hz2gw9u">
                                    Udfyldes automatisk baseret på ladeboks type
                                  </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-2128" data-mp="1tt75rdrkfhxa">
                                    Dine udgifter til materialer
                                  </p>}
                              </div>
                            </div>

                            <div className="pt-4 border-t border-stone-200" data-id="element-2129" data-mp="3vqheru3g47nh">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2130" data-mp="4j4vn9j05k4x">
                                <div data-id="element-2131" data-mp="4h1ym5jfmqqp">
                                  <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-2132" data-mp="3morjueacuqky">
                                    Timer brugt{' '}
                                    <span className="text-red-500" data-id="element-2133" data-mp="3rlrjo7gm2x3m">*</span>
                                  </label>
                                  <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required disabled={isReadOnly} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-2134" data-mp="3hpogcya0wsf3" />
                                  <p className="text-xs text-stone-500 mt-1.5" data-id="element-2135" data-mp="1x7dyra46fafi">
                                    Antal timer brugt på opgaven
                                  </p>
                                </div>

                                <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-2136" data-mp="1nfzqacu8z0a8">
                                  <div data-id="element-2137" data-mp="2fyx07cijygff">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2138" data-mp="2fzyctlf7vaz9">
                                      Samlet pris
                                    </p>
                                    <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-2139" data-mp="3tvzphutzz2j6">
                                      {calculations.totalPrice.toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                  </div>
                                  <div className="pt-3 border-t border-stone-100" data-id="element-2140" data-mp="qehskifrjfxj">
                                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2141" data-mp="ue044nlp2enp">
                                      Pris kun for timer
                                    </p>
                                    <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2142" data-mp="14dz8e8b2ziur">
                                      {(calculations.totalLaborCost ?? 0).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                      kr
                                    </p>
                                    <p className="text-xs text-stone-400 mt-1" data-id="element-2143" data-mp="7tpk08n1zuxu">
                                      Timepris × Timer
                                    </p>
                                  </div>
                                  {totalHoursWithWorkLog > 0 && <div className="pt-3 border-t border-stone-100" data-id="element-2144" data-mp="ct29701ede3l">
                                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-2145" data-mp="1t3o5kzfueoht">
                                        Timer brugt i alt
                                      </p>
                                      <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-2146" data-mp="1vrqdlrijxul4">
                                        {totalHoursWithWorkLog.toFixed(1)} timer
                                      </p>
                                    </div>}
                                </div>
                              </div>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2147" data-mp="25n1r66i84vra">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                pricing: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2148" data-mp="3c1zjn292xj5i">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2149" data-mp="2ocdp4s3njs3u" />
                    Vis Priser og timer
                    {totalHoursWithWorkLog > 0 && <span className="text-stone-500" data-id="element-2150" data-mp="2kb1sw3q9w8mo">
                        (Total: {totalHoursWithWorkLog.toFixed(1)}t)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* MATERIALS SECTION */}
            <AnimatePresence data-id="element-2151" data-mp="25fcht0mmlfwq">
              {expandedSections.materials ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2152" data-mp="1v8t2yxq0mvc2">
                  <div data-id="element-2153" data-mp="1hh4ur5iw0r0d">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  materials: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2154" data-mp="1hifu8t5avclo">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2155" data-mp="2cxwtp5k813a5">
                        Materialer
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2156" data-mp="28xcttu89jlkb">
                        {materials.length > 0 && <span className="text-sm text-stone-500" data-id="element-2157" data-mp="2mxnlgzhed46j">
                            {materials.length} materialer
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2158" data-mp="2vvplbbpe9qs8" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2159" data-mp="35n3njr8qd4p2">
                      {!isReadOnly && <>
                          <div className="flex items-center justify-between mb-4" data-id="element-2160" data-mp="30oiqg0orz14u">
                            <div className="flex-1" data-id="element-2161" data-mp="3fjyn8dgqpb4x" />
                            <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-2162" data-mp="3finfk2k6bj8j">
                              <ListIcon className="w-4 h-4" data-id="element-2163" data-mp="3ka2gjqn8k75w" />
                              {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                            </button>
                          </div>

                          <AnimatePresence mode="wait" data-id="element-2164" data-mp="3aio7os9e9v03">
                            {bulkMode ? <motion.div key="bulk" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-3" data-id="element-2165" data-mp="3fazkgvp8hba2">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2166" data-mp="36g3jd1yiisyl">
                                  <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-2167" data-mp="3bd3jyxclf3ib">
                                    📦 Tilføj stor ordre
                                  </p>
                                  <p className="text-xs text-blue-700" data-id="element-2168" data-mp="3l3ojqo406e2t">
                                    Skriv ét materiale per linje. Prisen lægges
                                    automatisk til materialer pris.
                                  </p>
                                </div>

                                <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m Stikkontakt Sikring 16A Kabelkanal 2m ..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-2169" data-mp="2xjxqiacalcr" />

                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-2170" data-mp="226jt9gl17wjw">
                                  <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-2171" data-mp="1xd8um2qdim32">
                                    💰 Pris for denne ordre (kr)
                                  </label>
                                  <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-2172" data-mp="2pwe63y5cpl45" />
                                  {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-2173" data-mp="2c9p466tbxzv0">
                                      <div className="flex items-center justify-between text-sm" data-id="element-2174" data-mp="2gairaxyy7amu">
                                        <span className="text-amber-700" data-id="element-2175" data-mp="ai3phyqjd2rz">
                                          Nuværende materialer pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-2176" data-mp="1z1g92bhopax8">
                                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-1" data-id="element-2177" data-mp="2rq5bqeye1ob3">
                                        <span className="text-amber-700" data-id="element-2178" data-mp="2mr12jobgrqkr">
                                          Ordre pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-2179" data-mp="xe72we3zqufq">
                                          +
                                          {parseFloat(bulkPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-2180" data-mp="g31tv5dr2xkc">
                                        <span className="font-semibold text-amber-900" data-id="element-2181" data-mp="pslgmm4l3bkh">
                                          Ny total:
                                        </span>
                                        <span className="font-bold text-amber-900" data-id="element-2182" data-mp="3ogr6v3e3hc1n">
                                          {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                    </div>}
                                </div>

                                <div className="flex gap-2" data-id="element-2183" data-mp="2t9rnr8bcgjrk">
                                  <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2 py-2.5" disabled={!bulkInput.trim()} data-id="element-2184" data-mp="2ob6pvfjkec6a">
                                    <PlusIcon className="w-4 h-4" data-id="element-2185" data-mp="29ohplbhhnuyu" />
                                    Tilføj alle materialer
                                    {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                                  </Button>
                                  <Button type="button" onClick={() => {
                            setBulkInput('');
                            setBulkPrice('');
                            setBulkMode(false);
                          }} variant="secondary" className="py-2.5 px-6" data-id="element-2186" data-mp="2ode6zz5fqkc4">
                                    Annuller
                                  </Button>
                                </div>
                              </motion.div> : <motion.div key="single" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-6" data-id="element-2187" data-mp="3hf5bzkau1ehn">
                                {/* Standard Materials */}
                                <div data-id="element-2188" data-mp="2h806ango5pwc">
                                  <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-2189" data-mp="2h91rhkrefsrs">
                                    Vælg fra standard
                                  </h4>
                                  {standardMaterials.length === 0 ? <p className="text-sm text-stone-500 italic" data-id="element-2190" data-mp="2uyvh45aqah2k">
                                      Ingen standard materialer endnu — tilføj
                                      dem under Information.
                                    </p> : <div className="flex flex-wrap gap-2" data-id="element-2191" data-mp="38y4z9aihvl96">
                                      {standardMaterials.map(preset => <motion.button key={preset.id} type="button" whileTap={!isReadOnly ? {
                              scale: 0.95
                            } : undefined} onClick={() => handleAddPresetMaterial(preset)} disabled={isReadOnly} className={`px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-sm font-medium text-stone-700 transition-all flex items-center gap-2 ${isReadOnly ? 'opacity-70 cursor-not-allowed' : 'hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'}`} data-id="element-2192" data-mp="3dvdep3c9db6q">
                                          <PackageIcon className="w-4 h-4" data-id="element-2193" data-mp="5k3n4sfud1uu" />
                                          {preset.name}
                                          {preset.price !== undefined && <span className="text-stone-500 font-normal" data-id="element-2194" data-mp="2x9j9ftcdobb8">
                                              ·{' '}
                                              {preset.price.toLocaleString('da-DK')}{' '}
                                              kr
                                            </span>}
                                        </motion.button>)}
                                    </div>}
                                </div>

                                <div className="pt-4 border-t border-stone-100" data-id="element-2195" data-mp="20jilfwju1yuz">
                                  <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-2196" data-mp="20hfmgn0pcve4">
                                    Tilføj manuelt
                                  </h4>
                                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-2197" data-mp="b4hmziucxh8x">
                                    <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Skriv materiale navn..." className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-2198" data-mp="g29dq9j9mxbj" />
                                    <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Pris (kr)" className="w-full sm:w-32 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-2199" data-mp="gq25qw6z4uy6" />
                                    <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center justify-center gap-2" disabled={!materialInput.trim()} data-id="element-2200" data-mp="1x2w8khiy099g">
                                      <PlusIcon className="w-4 h-4" data-id="element-2201" data-mp="1e9sckpi95kbo" />
                                      Tilføj
                                    </Button>
                                  </div>
                                </div>

                                {materialPriceInput && materialPrice && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-2202" data-mp="19cjqidtzgw55">
                                    <div className="flex items-center justify-between text-sm" data-id="element-2203" data-mp="3vseci02h1fu9">
                                      <span className="text-green-700" data-id="element-2204" data-mp="3rrch5ubodnxk">
                                        Nuværende total:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-2205" data-mp="1pyektoiowd7v">
                                        {parseFloat(materialPrice).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1" data-id="element-2206" data-mp="2twi6x3s51vw0">
                                      <span className="text-green-700" data-id="element-2207" data-mp="2xydayg1gendh">
                                        Tilføjes:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-2208" data-mp="32v3p2xtp78w9">
                                        +
                                        {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-2209" data-mp="1vjm85ysj7ewf">
                                      <span className="font-semibold text-green-900" data-id="element-2210" data-mp="1qlazvshvxcgb">
                                        Ny total:
                                      </span>
                                      <span className="font-bold text-green-900" data-id="element-2211" data-mp="20i77zx3vfwkc">
                                        {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                  </motion.div>}
                              </motion.div>}
                          </AnimatePresence>
                        </>}

                      {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-2212" data-mp="3t6onloh9aq5l">
                          <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2213" data-mp="20dssa2ualmp">
                            {showMaterials ? <>
                                <EyeOffIcon className="w-4 h-4" data-id="element-2214" data-mp="39f31pt44o8ff" />
                                Skjul materialer ({materials.length})
                              </> : <>
                                <EyeIcon className="w-4 h-4" data-id="element-2215" data-mp="2zizpfufkater" />
                                Se materialer ({materials.length})
                              </>}
                          </Button>

                          <AnimatePresence data-id="element-2216" data-mp="3s8geqj7vxu6r">
                            {showMaterials && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2
                      }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2217" data-mp="3ndatznq7ylk9">
                                <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2218" data-mp="157qgigmu36rv">
                                  {materials.map((material, index) => <motion.li key={index} initial={{
                            opacity: 0,
                            x: -10
                          }} animate={{
                            opacity: 1,
                            x: 0
                          }} exit={{
                            opacity: 0,
                            x: -10
                          }} transition={{
                            delay: index * 0.02
                          }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2219" data-mp="1f0p6o2xzb5dl" data-mpi={index}>
                                      {editingMaterialIndex === index ? <div className="flex items-center gap-2" data-id="element-2220" data-mp="34mwnujzcby1a">
                                          <input type="text" value={editingMaterialValue} onChange={e => setEditingMaterialValue(e.target.value)} onKeyPress={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveEditMaterial();
                                } else if (e.key === 'Escape') {
                                  handleCancelEditMaterial();
                                }
                              }} className="flex-1 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-2221" data-mp="39m9ab3mav33c" />
                                          <button type="button" onClick={handleSaveEditMaterial} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-2222" data-mp="1kue5y6png1f2">
                                            <CheckIcon className="w-4 h-4" data-id="element-2223" data-mp="1ysuhqc2dci42" />
                                          </button>
                                          <button type="button" onClick={handleCancelEditMaterial} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded transition-colors" data-id="element-2224" data-mp="172hgkafd85md">
                                            <XCircle className="w-4 h-4" data-id="element-2225" data-mp="jcdnuy3ptqa5" />
                                          </button>
                                        </div> : <div className="flex items-center justify-between" data-id="element-2226" data-mp="fu6cxjhaxzm0">
                                          <span className="text-sm text-stone-900" data-id="element-2227" data-mp="awo3lrzdhidh">
                                            {material}
                                          </span>
                                          {!isReadOnly && <div className="flex items-center gap-1" data-id="element-2228" data-mp="3t213rep8qrqg">
                                              <button type="button" onClick={() => handleStartEditMaterial(index)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger materiale" data-id="element-2229" data-mp="3oardqczs5me1">
                                                <EditIcon className="w-4 h-4" data-id="element-2230" data-mp="19b4pw2mvmi1f" />
                                              </button>
                                              <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-2231" data-mp="zjh6mp0h47b1">
                                                <XCircle className="w-4 h-4" data-id="element-2232" data-mp="ll0weo3ejyo5" />
                                              </button>
                                            </div>}
                                        </div>}
                                    </motion.li>)}
                                </ul>
                              </motion.div>}
                          </AnimatePresence>
                        </div>}

                      {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-2233" data-mp="mys8otqkzjyu">
                          Ingen materialer tilføjet endnu
                        </p>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2234" data-mp="rxlkmhq7z2ww">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                materials: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2235" data-mp="3ngtq4jrskqn0">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2236" data-mp="f21m8du4k9ps" />
                    Vis Materialer
                    {materials.length > 0 && <span className="text-stone-500" data-id="element-2237" data-mp="k1o4x2sq2i5z">
                        ({materials.length} materialer)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* WORK LOG SECTION */}
            <AnimatePresence data-id="element-2238" data-mp="2edp89o3b9whk">
              {expandedSections.workLog ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2239" data-mp="29fnnmyq8lp3z">
                  <div data-id="element-2240" data-mp="2nff7vh7ck1qf">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  workLog: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2241" data-mp="2nex0bof8i137">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2242" data-mp="2o9zn7nb9wbaj">
                        Arbejdslog
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2243" data-mp="2ajcqu1szixhm">
                        {workLog.length > 0 && <span className="text-sm text-stone-500" data-id="element-2244" data-mp="2fhe5majdoana">
                            {workLog.length} entries
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2245" data-mp="3770whkbv0xka" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2246" data-mp="2y6uizyg5vxl4">
                      {!isReadOnly && <div className="space-y-3" data-id="element-2247" data-mp="1hsumkxgiyq2m">
                          <div className="flex gap-2" data-id="element-2248" data-mp="1hvgu4x3f15ku">
                            <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2249" data-mp="1msqq6vlb8fhn" />
                            <input type="number" step="0.5" value={workLogHours} onChange={e => setWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2250" data-mp="1j8gi8czbtvfa" />
                          </div>
                          <textarea value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} placeholder="Beskriv hvad der blev lavet...&#10;Du kan skrive flere linjer her." rows={4} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-2251" data-mp="7v4ee23khct4" />
                          <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2 w-full justify-center" disabled={!workLogDescription.trim()} data-id="element-2252" data-mp="1h0x6vgw6tepm">
                            <PlusIcon className="w-4 h-4" data-id="element-2253" data-mp="2nituit6gvpc7" />
                            Tilføj til arbejdslog
                          </Button>
                        </div>}

                      {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-2254" data-mp="2m0dchgl3cp3h">
                          <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2255" data-mp="2m1wsd62u4y4q">
                            {showWorkLog ? <>
                                <EyeOffIcon className="w-4 h-4" data-id="element-2256" data-mp="34wliwnpub60d" />
                                Skjul arbejdslog ({workLog.length})
                              </> : <>
                                <FileTextIcon className="w-4 h-4" data-id="element-2257" data-mp="68xurjpoadz1" />
                                Se arbejdslog ({workLog.length})
                              </>}
                          </Button>

                          <AnimatePresence data-id="element-2258" data-mp="b6monjax2xi9">
                            {showWorkLog && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.2
                      }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2259" data-mp="64xc1z81qbs2">
                                <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2260" data-mp="1l2t7lyk8qpdu">
                                  {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => {
                            const originalIndex = workLog.findIndex(e => e === entry);
                            return <motion.li key={originalIndex} initial={{
                              opacity: 0,
                              x: -10
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} exit={{
                              opacity: 0,
                              x: -10
                            }} transition={{
                              delay: index * 0.02
                            }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2261" data-mp="1g59ret3zg2j9" data-mpi={index}>
                                          {editingWorkLogIndex === originalIndex ? <div className="space-y-2" data-id="element-2262" data-mp="1jmffl600pi9u">
                                              <div className="flex gap-2" data-id="element-2263" data-mp="1jo8kafr2lsf7">
                                                <input type="date" value={editingWorkLogDate} onChange={e => setEditingWorkLogDate(e.target.value)} className="w-40 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2264" data-mp="19iqvpok1s0wu" />
                                                <input type="number" step="0.5" value={editingWorkLogHours} onChange={e => setEditingWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2265" data-mp="luhd0vi4rt9x" />
                                              </div>
                                              <textarea value={editingWorkLogDescription} onChange={e => setEditingWorkLogDescription(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" autoFocus data-id="element-2266" data-mp="2irdgc0fvtl59" />
                                              <div className="flex gap-2" data-id="element-2267" data-mp="fagu4nfb7tx0">
                                                <button type="button" onClick={handleSaveEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors" data-id="element-2268" data-mp="fcjhezp2v8y1">
                                                  Gem
                                                </button>
                                                <button type="button" onClick={handleCancelEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-stone-600 bg-stone-100 hover:bg-stone-200 rounded transition-colors" data-id="element-2269" data-mp="1jk9hz0vju6fp">
                                                  Annuller
                                                </button>
                                              </div>
                                            </div> : <div className="flex items-start justify-between gap-3" data-id="element-2270" data-mp="2oiwkc9kpd9g6">
                                              <div className="flex-1" data-id="element-2271" data-mp="2ari2w76g1gb8">
                                                <div className="flex items-center gap-2 mb-1" data-id="element-2272" data-mp="2atcmni5zb2dq">
                                                  <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-2273" data-mp="2etcziydfj5io" />
                                                  <span className="text-xs font-medium text-teal-700" data-id="element-2274" data-mp="32jh71gtqnpey">
                                                    {new Date(entry.date).toLocaleDateString('da-DK', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                                  </span>
                                                  {entry.hours && <>
                                                      <span className="text-xs text-stone-400" data-id="element-2275" data-mp="2ccu6f1bi8ib">
                                                        •
                                                      </span>
                                                      <ClockIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2276" data-mp="g5kq2iwflq5t" />
                                                      <span className="text-xs font-medium text-blue-700" data-id="element-2277" data-mp="3ftc4kv11pdah">
                                                        {entry.hours}t
                                                      </span>
                                                    </>}
                                                </div>
                                                <p className="text-sm text-stone-900" data-id="element-2278" data-mp="33a84ozcoer0w">
                                                  {entry.description}
                                                </p>
                                              </div>
                                              {!isReadOnly && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2279" data-mp="2olc2vk17qa8q">
                                                  <button type="button" onClick={() => handleStartEditWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger log" data-id="element-2280" data-mp="2jnitki4m5048">
                                                    <EditIcon className="w-4 h-4" data-id="element-2281" data-mp="1r09f5v7khb9m" />
                                                  </button>
                                                  <button type="button" onClick={() => handleRemoveWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern log" data-id="element-2282" data-mp="2joeyqemvwu7v">
                                                    <XCircle className="w-4 h-4" data-id="element-2283" data-mp="10qa2wd9hpprp" />
                                                  </button>
                                                </div>}
                                            </div>}
                                        </motion.li>;
                          })}
                                </ul>
                              </motion.div>}
                          </AnimatePresence>
                        </div>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2284" data-mp="1ohxkg4kv3ohb">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                workLog: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2285" data-mp="2xqu3k0ciu4dd">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2286" data-mp="3lg68up4q9sae" />
                    Vis Arbejdslog
                    {workLog.length > 0 && <span className="text-stone-500" data-id="element-2287" data-mp="3qc3n8pwfkokj">
                        ({workLog.length} entries)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* ATTACHMENTS SECTION */}
            <AnimatePresence data-id="element-2288" data-mp="14urib85j6x30">
              {expandedSections.attachments ? <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2289" data-mp="zv552yppdgu7">
                  <div data-id="element-2290" data-mp="2g8vkh1m9vrj5">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  attachments: false
                })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2291" data-mp="2g9513ylfgmc6">
                      <h3 className="text-lg font-semibold text-stone-900" data-id="element-2292" data-mp="4sldtlddpmst">
                        Bilag
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-2293" data-mp="3mxewttlf8bst">
                        {attachments.length > 0 && <span className="text-sm text-stone-500" data-id="element-2294" data-mp="3r1e73ug3yedq">
                            {attachments.length} bilag
                          </span>}
                        <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2295" data-mp="3i068fb9ojbj4" />
                      </div>
                    </button>

                    <div className="space-y-3" data-id="element-2296" data-mp="1g89z88azidri">
                      {!isReadOnly && <div className="flex justify-end mb-4" data-id="element-2297" data-mp="30pj4zprzr58b">
                          <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={isUploading} data-id="element-2298" data-mp="30ny7pseal86r" />
                          <label htmlFor="file-upload" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2299" data-mp="35n2mcyjzjy4h">
                            {isUploading ? <>
                                <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2300" data-mp="1z0fheo28jerh" />
                                Uploader...
                              </> : <>
                                <UploadIcon className="w-4 h-4" data-id="element-2301" data-mp="187f1fwoyu90n" />
                                Tilføj bilag
                              </>}
                          </label>
                        </div>}

                      {attachments.length > 0 ? <ul className="space-y-2" data-id="element-2302" data-mp="1mw1flv2uwuqa">
                          {attachments.map((attachment, index) => <motion.li key={attachment.storagePath} initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-2303" data-mp="pbf74f8imtdw" data-mpi={index}>
                              <div className="flex items-center gap-3 overflow-hidden" data-id="element-2304" data-mp="303zt7hi2brg">
                                <div className="p-2 bg-stone-100 rounded-lg flex-shrink-0" data-id="element-2305" data-mp="3v4pqte6xsjmk">
                                  <FileIcon className="w-5 h-5 text-stone-600" data-id="element-2306" data-mp="3qdg34j4y7qdb" />
                                </div>
                                <div className="min-w-0" data-id="element-2307" data-mp="3led8lsmlh2jl">
                                  <p className="text-sm font-medium text-stone-900 truncate" data-id="element-2308" data-mp="3gftl3nscrip4">
                                    {attachment.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-stone-500" data-id="element-2309" data-mp="3bjbioukhvwfy">
                                    <span data-id="element-2310" data-mp="37h83buupg7pe">
                                      {new Date(attachment.uploadedAt).toLocaleDateString('da-DK', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                                    </span>
                                    {attachment.size && <>
                                        <span data-id="element-2311" data-mp="la30s4917elx">•</span>
                                        <span data-id="element-2312" data-mp="qbimzzmy3r5w">
                                          {formatFileSize(attachment.size)}
                                        </span>
                                      </>}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-2313" data-mp="2jtn9y7so0ex">
                                <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-stone-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Download" data-id="element-2314" data-mp="7ehbrx2ssuti">
                                  <DownloadIcon className="w-4 h-4" data-id="element-2315" data-mp="1j5ox9f09zs13" />
                                </a>
                                {!isReadOnly && <button type="button" onClick={() => handleDeleteAttachment(attachment, index)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet" data-id="element-2316" data-mp="1e62ekq1ohjkw">
                                    <Trash2Icon className="w-4 h-4" data-id="element-2317" data-mp="qfyeutrm23z6" />
                                  </button>}
                              </div>
                            </motion.li>)}
                        </ul> : <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200" data-id="element-2318" data-mp="gufd545vm496">
                          <PaperclipIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" data-id="element-2319" data-mp="2uxqauc5512g" />
                          <p className="text-sm text-stone-500" data-id="element-2320" data-mp="6w9cepvt345w">
                            Ingen bilag tilføjet endnu
                          </p>
                        </div>}
                    </div>
                  </div>
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2321" data-mp="lrfdqzjyiesw">
                  <button type="button" onClick={() => setExpandedSections({
                ...expandedSections,
                attachments: true
              })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2322" data-mp="330a0w0436vm8">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2323" data-mp="2f2up10ko3rlu" />
                    Vis Bilag
                    {attachments.length > 0 && <span className="text-stone-500" data-id="element-2324" data-mp="2u1534w0cn6b4">
                        ({attachments.length} bilag)
                      </span>}
                  </button>
                </motion.div>}
            </AnimatePresence>

            {/* Vigtige bemærkninger */}
            <div data-id="element-2325" data-mp="49qhjfjght05">
              <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} disabled={isReadOnly} data-id="element-2326" data-mp="4a9vym9wdz9f" />
            </div>
          </form>
        </div>
      </div>

      {/* Bottom action buttons */}
      <div className="mt-8 flex gap-3" data-id="element-2327" data-mp="2nzmqbi0dl5m4">
        <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-2328" data-mp="2nwh182rqy3qe">
          Annuller
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit as any} className="flex-1" disabled={isReadOnly} data-id="element-2329" data-mp="2xvgczu7t6fhm">
          Gem ændringer
        </Button>
      </div>
    </div>;
  <div data-id="element-2330" data-mp="3bofnfhsqhjib">;</div>;
}